import { useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Group, Progress, Stack, Tabs, Text } from '@mantine/core'
import type { ProjectAISettingsResponse } from '@/types/api'
import { AiInstructionsTab } from './AiInstructionsTab'
import { AiKnowledgeTab } from './AiKnowledgeTab'
import { AiVariablesTab } from './AiVariablesTab'

type Props = {
  projectId: string
  projectAiId: string
  canEdit: boolean
  refreshKey: number
  settings: ProjectAISettingsResponse | null
  onUpdated: () => void
  active?: boolean
}

export function AiContentTab({
  projectId,
  projectAiId,
  canEdit,
  refreshKey,
  settings,
  onUpdated,
  active = true,
}: Props) {
  const variables = Array.isArray(settings?.variables) ? settings.variables : []
  const [wizardMode, setWizardMode] = useState(true)
  const [wizardStep, setWizardStep] = useState(0)
  const [contentTab, setContentTab] = useState<'instructions' | 'knowledge' | 'variables'>('instructions')

  const instructionsCount = settings?.instructions?.filter((x) => x.is_active !== false).length ?? 0
  const kbCount = settings?.knowledge_base_items?.filter((x) => x.is_active !== false).length ?? 0
  const varsCount = variables.length

  const steps = useMemo(
    () => [
      { key: 'instructions', title: 'Инструкции', done: instructionsCount > 0, hint: `${instructionsCount} активных` },
      { key: 'knowledge', title: 'База знаний', done: kbCount > 0, hint: `${kbCount} активных` },
      { key: 'variables', title: 'Переменные', done: varsCount > 0, hint: `${varsCount} шт.` },
    ],
    [instructionsCount, kbCount, varsCount],
  )

  const doneCount = steps.filter((s) => s.done).length
  const progress = Math.round((doneCount / steps.length) * 100)
  const clampedStep = Math.min(Math.max(wizardStep, 0), steps.length - 1)
  const activeStep = steps[clampedStep]?.key ?? 'instructions'
  const activeContentTab = wizardMode ? (activeStep as 'instructions' | 'knowledge' | 'variables') : contentTab

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light">
        Единый шаблон контента бота: здесь ведутся инструкции, база знаний и переменные проекта.
        Загрузка/выгрузка Excel сохранена в каждом блоке.
      </Alert>
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="xs">
          <Text fw={600}>Мастер заполнения</Text>
          <Button size="compact-sm" variant="subtle" onClick={() => setWizardMode((v) => !v)}>
            {wizardMode ? 'Обычный режим' : 'Мастер-режим'}
          </Button>
        </Group>
        <Progress value={progress} mb="sm" />
        <Group gap="xs" wrap="wrap">
          {steps.map((s, i) => (
            <Badge
              key={s.key}
              variant={i === clampedStep ? 'filled' : 'light'}
              color={s.done ? 'green' : i === clampedStep ? 'blue' : 'gray'}
              style={{ cursor: 'pointer' }}
              onClick={() => setWizardStep(i)}
            >
              {i + 1}. {s.title}: {s.hint}
            </Badge>
          ))}
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Готовность контента: {doneCount}/{steps.length}. Рекомендуется заполнить все шаги.
        </Text>
      </Card>

      <Tabs value={activeContentTab} onChange={(v) => v && setContentTab(v as 'instructions' | 'knowledge' | 'variables')}>
        <Tabs.List>
          <Tabs.Tab value="instructions">1. Инструкции и промпт</Tabs.Tab>
          <Tabs.Tab value="knowledge">2. База знаний</Tabs.Tab>
          <Tabs.Tab value="variables">3. Переменные</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="instructions" pt="md">
          <AiInstructionsTab
            projectId={projectId}
            canEdit={canEdit}
            refreshKey={refreshKey}
            settings={settings}
            onSettingsUpdated={onUpdated}
            tabActive={active}
          />
        </Tabs.Panel>
        <Tabs.Panel value="knowledge" pt="md">
          {projectAiId ? (
            <AiKnowledgeTab
              projectId={projectId}
              projectAiId={projectAiId}
              canEdit={canEdit}
              refreshKey={refreshKey}
            />
          ) : (
            <Text size="sm" c="dimmed">
              Сначала сохраните настройки AI проекта.
            </Text>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="variables" pt="md">
          <AiVariablesTab
            projectId={projectId}
            variables={variables}
            canEdit={canEdit}
            onUpdated={onUpdated}
          />
        </Tabs.Panel>
      </Tabs>
      {wizardMode ? (
        <Group justify="space-between">
          <Button
            variant="default"
            disabled={clampedStep === 0}
            onClick={() => setWizardStep((s) => Math.max(0, s - 1))}
          >
            Назад
          </Button>
          <Button
            disabled={clampedStep >= steps.length - 1}
            onClick={() => setWizardStep((s) => Math.min(steps.length - 1, s + 1))}
          >
            Далее
          </Button>
        </Group>
      ) : null}
    </Stack>
  )
}
